import React, { useMemo } from 'react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useProjectAnalytics } from '@/hooks/useProjectAnalytics';
import { EmptyState } from '@/components/analytics/EmptyState';
import LoadingState from '@/components/ui/LoadingState';
import { CriticalActivitiesTable } from '@/components/analytics/CriticalActivitiesTable';
import { WBSHotspotsFloatTable } from '@/components/analytics/WBSHotspotsFloatTable';
import { AnalyticsStatusRow } from '@/components/analytics/AnalyticsStatusRow';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { TableScaffold } from '@/components/ui/TableScaffold';
import { resolveHealthStatus } from '@/constants/analyticsThresholds';

const CriticalPathPage: React.FC = () => {
  const { selectedProject, selectedBaselineId } = useGlobalProject();
  const { loading, merged } = useProjectAnalytics();
  const errors = merged?.errors ?? [];

  const loaded = useMemo(() => ({
    schedule: !!merged,
    activities: !!(merged?.criticalActivities?.length || merged?.wbsHotspots?.length),
    risk: !!merged,
    baseline: !!selectedBaselineId,
  }), [merged, selectedBaselineId]);

  /* ── Aggregate counts for the summary strip ── */
  const bandCounts = useMemo(() => {
    const acts = merged?.criticalActivities ?? [];
    let critical = 0;
    let nearCritical = 0;
    let monitor = 0;
    for (const a of acts) {
      if (a.criticalityBand === 'critical') critical++;
      else if (a.criticalityBand === 'near-critical') nearCritical++;
      else monitor++;
    }
    return { critical, nearCritical, monitor, total: acts.length };
  }, [merged]);

  const statStatus = (s: 'ok' | 'warning' | 'critical' | 'unknown'): 'ok' | 'warning' | 'critical' | 'neutral' =>
    s === 'unknown' ? 'neutral' : s;

  if (!selectedProject) {
    return (
      <div className="flex flex-col h-full">
        <EmptyState />
      </div>
    );
  }

  if (loading && !merged) {
    return (
      <div className="flex flex-col h-full">
        <LoadingState message="Loading critical path…" size="lg" fullScreen={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Critical path hotspots"
        subtitle="Critical, near-critical, and monitor-band activities by float and WBS risk."
        meta={<AnalyticsStatusRow loaded={loaded} errorCount={errors.length} errorMessages={errors} />}
      />

      {/* ── Signal strip: band summary ── */}
      {merged && (
        <div className="grid grid-cols-4 gap-2">
          <StatCard
            label="Critical (driving path / ≤0 hr)"
            value={bandCounts.critical}
            status={bandCounts.critical > 0 ? 'critical' : 'ok'}
          />
          <StatCard
            label="Near-Critical (≤5d / 40 hr)"
            value={bandCounts.nearCritical}
            status={bandCounts.nearCritical > 0 ? 'warning' : 'ok'}
          />
          <StatCard
            label="Monitor (5–10d / 40–80 hr)"
            value={bandCounts.monitor}
            status="neutral"
          />
          <StatCard
            label="Critical + Near-Critical"
            value={bandCounts.critical + bandCounts.nearCritical}
            delta={`of ${merged.scheduleMetrics?.criticalOrNearCriticalCount ?? bandCounts.total} total`}
            status={statStatus(resolveHealthStatus('criticalNearCritical', bandCounts.critical + bandCounts.nearCritical))}
          />
        </div>
      )}

      {merged && (
        <>
          <TableScaffold toolbar={<span className="text-xs font-medium text-muted-foreground">Critical / Near-Critical / Monitor Activities</span>}>
            <CriticalActivitiesTable
              activities={merged.criticalActivities}
              loading={loading}
            />
          </TableScaffold>
          <TableScaffold toolbar={<span className="text-xs font-medium text-muted-foreground">WBS Hotspots (Float)</span>}>
            <WBSHotspotsFloatTable rows={merged.wbsHotspots} loading={loading} />
          </TableScaffold>
        </>
      )}
      {!merged && !loading && (
        <p className="text-xs text-muted-foreground">No schedule data available. Select a project with activities.</p>
      )}
    </div>
  );
};

export default CriticalPathPage;
